import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PlyrWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { setShowClosedCaptions } from 'store/actions/student';

const DEFAULT_CC_SIZE = 16;

const VideoPlayer = ({
  src,
  title = '',
  captionsSrc,
  narrationSrc,
  narrationVtt,
  narrationTitle,
  autoPlay = false,
  showSettings = true,
  withNativeControls = true,
  initialShowCC = false,
  initialCCSize = DEFAULT_CC_SIZE,
  initialShowNarration = false,
  onDone,
  onLoad,
  onError,
  containerStyle = {},
  plyrStyle = {},
  volume = 0.5
}) => {
  const defaultShowCC = useSelector(state => state.student.showCC);
  const dispatch = useDispatch();
  const [showCC, setShowCC] = useState(defaultShowCC || initialShowCC);
  const [ccSize, setCCSize] = useState(initialCCSize);
  const [showNarration, setShowNarration] = useState(initialShowNarration);

  const handleSetShowClosedCaptions = value => dispatch(setShowClosedCaptions(value));

  const enableCaptions = () => {
    handleSetShowClosedCaptions(true);
    setShowCC(true);
  };
  const disableCaptions = () => {
    handleSetShowClosedCaptions(false);
    setShowCC(false);
  };
  const updateCCSize = size => setCCSize(size);
  const enableNarration = () => setShowNarration(true);
  const disableNarration = () => setShowNarration(false);

  const PlyrProps = {
    src,
    type: 'video/mp4',
    title,
    mimeType: 'video/mp4',
    onCaptionsEnabled: enableCaptions,
    onCaptionsDisabled: disableCaptions,
    onCaptionsSizeChange: updateCCSize,
    onError,
    narration: narrationSrc
      ? {
          src: narrationSrc,
          captions: [
            {
              src: narrationVtt,
              label: narrationTitle,
              kind: 'captions'
            }
          ],
          onNarrationEnabled: enableNarration,
          onNarrationDisabled: disableNarration
        }
      : {},
    options: {
      narrationsAsDefault: showNarration && !!narrationSrc,
      withNativeControls,
      locale: getLocale(),
      captionButton: !!captionsSrc,
      captions: { active: defaultShowCC ?? showCC, styles: { fontSize: ccSize } },
      showSettings,
      autoplay: autoPlay,
      containerStyles: containerStyle,
      plyrStyle: plyrStyle
    },
    captions: captionsSrc
      ? [
          {
            kind: 'captions',
            src: captionsSrc,
            label: title
          }
        ]
      : undefined,
    config: {
      volume
    },
    onDone,
    onLoad
  };

  return <PlyrWithClosedCaptions {...PlyrProps} />;
};

import PropTypes from 'prop-types';

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  captionsSrc: PropTypes.string,
  narrationSrc: PropTypes.string,
  narrationVtt: PropTypes.string,
  narrationTitle: PropTypes.string,
  autoPlay: PropTypes.bool,
  showSettings: PropTypes.bool,
  withNativeControls: PropTypes.bool,
  initialShowCC: PropTypes.bool,
  initialCCSize: PropTypes.number,
  initialShowNarration: PropTypes.bool,
  onDone: PropTypes.func,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  containerStyle: PropTypes.object,
  plyrStyle: PropTypes.object,
  volume: PropTypes.number
};

export default VideoPlayer;
